import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN  = process.env.REACT_APP_MAPBOX_TOKEN || '';
const POLL_INTERVAL = 3000;

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PreviewData {
  countryCode:     string | null;
  /** ISO 3166-1 alpha-2 — Mapbox `country_boundaries` matches this reliably */
  iso2:            string | null;
  iso3:            string | null;
  originalFeature: any | null;
  proposedFeature: any;
  createdAt:       string;
  updatedAt:       string;
  expiresAt:       string;
}

type ViewMode = 'sideBySide' | 'overlay';

// ─── Bbox helper ───────────────────────────────────────────────────────────────

function computeBbox(features: any[]): [number, number, number, number] {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  features.forEach(f => {
    const geom = f?.geometry;
    if (!geom?.coordinates) return;
    const rings: number[][][] = [];
    if (geom.type === 'Polygon')           geom.coordinates.forEach((r: number[][]) => rings.push(r));
    else if (geom.type === 'MultiPolygon') geom.coordinates.forEach((p: number[][][]) => p.forEach((r: number[][]) => rings.push(r)));
    rings.forEach(ring => ring.forEach(([lng, lat]) => {
      if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
    }));
  });
  const pw = (maxLng - minLng) * 0.08 || 0.5;
  const ph = (maxLat - minLat) * 0.08 || 0.5;
  return [minLng - pw, minLat - ph, maxLng + pw, maxLat + ph];
}

// ─── Reference layer (blue dashed) ───────────────────────────────────────────
// Strategy A: Mapbox Countries v1 — filter on `iso_3166_1` (alpha-2). Official
// examples use alpha-2, not alpha-3 (alpha-3 can be missing in tiles).
// Docs: https://docs.mapbox.com/data/tilesets/reference/mapbox-countries-v1/
// Strategy B: GeoJSON from /api/mcp/get_reference_boundary (OSM via geoBoundaries).

/** Filter for one country polygon — prefer iso2 (KW), else iso3 (KWT). */
function buildMapboxCountryFilter(iso2: string | null, iso3: string | null): any | null {
  const country: any = iso2
    ? ['==', ['get', 'iso_3166_1'], iso2]
    : iso3
      ? ['==', ['get', 'iso_3166_1_alpha_3'], iso3]
      : null;
  if (!country) return null;

  return [
    'all',
    // disputed is string "false" in tile docs; tolerate boolean false
    ['any',
      ['==', ['get', 'disputed'], 'false'],
      ['==', ['get', 'disputed'], false],
    ],
    ['any',
      ['==', ['get', 'worldview'], 'all'],
      ['in', 'US', ['get', 'worldview']],
    ],
    country,
  ];
}

function addMapboxTilesetRef(m: mapboxgl.Map, iso2: string | null, iso3: string | null) {
  const filter = buildMapboxCountryFilter(iso2, iso3);
  if (!filter) return;

  try {
    if (!m.getSource('mapbox-ref')) {
      m.addSource('mapbox-ref', {
        type: 'vector',
        url:  'mapbox://mapbox.country-boundaries-v1',
      });
    }
    const linePaint: any = {
      'line-color':     '#2563eb',
      'line-width':     ['interpolate', ['linear'], ['zoom'], 2, 1.5, 6, 2.5, 10, 4],
      'line-opacity':   0.95,
      'line-dasharray': [4, 2],
    };
    if (!m.getLayer('mapbox-ref-fill')) {
      m.addLayer({
        id: 'mapbox-ref-fill', type: 'fill', source: 'mapbox-ref', 'source-layer': 'country_boundaries',
        filter, paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.12 },
      });
    }
    if (!m.getLayer('mapbox-ref-line')) {
      m.addLayer({
        id: 'mapbox-ref-line', type: 'line', source: 'mapbox-ref', 'source-layer': 'country_boundaries',
        filter, paint: linePaint,
      });
    }
  } catch (e) {
  }
}

function addGeoJsonRef(m: mapboxgl.Map, refFeature: any) {
  if (!refFeature?.geometry?.coordinates) return;
  try {
    if (!m.getSource('geojson-ref')) {
      m.addSource('geojson-ref', { type: 'geojson', data: refFeature });
    }
    if (!m.getLayer('geojson-ref-fill')) {
      m.addLayer({ id: 'geojson-ref-fill', type: 'fill', source: 'geojson-ref', paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.10 } });
    }
    if (!m.getLayer('geojson-ref-line')) {
      m.addLayer({ id: 'geojson-ref-line', type: 'line', source: 'geojson-ref', paint: { 'line-color': '#3b82f6', 'line-width': 3, 'line-opacity': 0.9, 'line-dasharray': [4, 3] } });
    }
  } catch (e) {
  }
}

function addReferenceLayer(m: mapboxgl.Map, iso2: string | null, iso3: string | null, refFeature: any) {
  if (iso2 || iso3) addMapboxTilesetRef(m, iso2, iso3);
  if (refFeature?.geometry?.coordinates) addGeoJsonRef(m, refFeature);
}

// ─── Single Mapbox map panel ───────────────────────────────────────────────────

interface MapPanelProps {
  label:      string;
  color:      string;
  feature:    any | null;
  bbox:       [number, number, number, number];
  iso2:       string | null;
  iso3:       string | null;
  refFeature: any | null;
  missing?:   boolean;
}

function MapPanel({ label, color, feature, bbox, iso2, iso3, refFeature, missing }: MapPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<mapboxgl.Map | null>(null);
  const mountedRef   = useRef(false);

  useEffect(() => {
    if (!containerRef.current || mountedRef.current || missing) return;
    mountedRef.current = true;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const m = new mapboxgl.Map({
      container:        containerRef.current,
      style:            'mapbox://styles/mapbox/light-v11',
      projection:       'globe' as any,
      bounds:           [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
      fitBoundsOptions: { padding: 40, maxZoom: 12 },
      attributionControl: false,
      interactive:      true,
    });

    m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    m.once('load', () => {
      // Layer 1: Reference (blue dashed) — Mapbox tileset + GeoJSON fallback
      addReferenceLayer(m, iso2, iso3, refFeature);

      // Layer 2: GeoTrust data (orange or green)
      if (feature?.geometry?.coordinates) {
        m.addSource('border', { type: 'geojson', data: feature });
        m.addLayer({ id: 'border-fill', type: 'fill', source: 'border', paint: { 'fill-color': color, 'fill-opacity': 0.40 } });
        m.addLayer({ id: 'border-line', type: 'line', source: 'border', paint: { 'line-color': color, 'line-width': 2.5, 'line-opacity': 0.95 } });
      }
    });

    mapRef.current = m;
    return () => {
      m.remove();
      mapRef.current = null;
      mountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasRef = !!(iso2 || iso3 || refFeature?.geometry?.coordinates);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        background: '#fff', borderBottom: '1px solid #e5e7eb', borderRadius: '10px 10px 0 0',
      }}>
        <div style={{ width: 14, height: 14, borderRadius: 3, background: color, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: hasRef ? '#3b82f6' : '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
          {hasRef
            ? (<><svg width="16" height="10" viewBox="0 0 16 10" fill="none"><line x1="0" y1="5" x2="16" y2="5" stroke="#3b82f6" strokeWidth="2.5" strokeDasharray="4 3"/></svg>reference</>)
            : 'no reference'
          }
        </span>
      </div>
      <div
        ref={containerRef}
        style={{
          flex: 1, position: 'relative', borderRadius: '0 0 10px 10px', overflow: 'hidden',
          background: missing ? '#f3f4f6' : '#e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {missing && (
          <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '0 24px' }}>
            No original feature included in this preview.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Overlay map (all 3 layers) ───────────────────────────────────────────────

interface OverlayMapProps {
  originalFeature: any | null;
  proposedFeature: any;
  bbox:            [number, number, number, number];
  iso2:            string | null;
  iso3:            string | null;
  refFeature:      any | null;
}

function OverlayMap({ originalFeature, proposedFeature, bbox, iso2, iso3, refFeature }: OverlayMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<mapboxgl.Map | null>(null);
  const mountedRef   = useRef(false);

  useEffect(() => {
    if (!containerRef.current || mountedRef.current) return;
    mountedRef.current = true;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const m = new mapboxgl.Map({
      container:        containerRef.current,
      style:            'mapbox://styles/mapbox/light-v11',
      projection:       'globe' as any,
      bounds:           [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
      fitBoundsOptions: { padding: 40, maxZoom: 12 },
      attributionControl: false,
      interactive:      true,
    });

    m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    m.once('load', () => {
      // Layer 1: Reference (blue dashed) — Mapbox tileset + GeoJSON fallback
      addReferenceLayer(m, iso2, iso3, refFeature);

      // Layer 2: Original (orange)
      if (originalFeature?.geometry?.coordinates) {
        m.addSource('orig', { type: 'geojson', data: originalFeature });
        m.addLayer({ id: 'orig-fill', type: 'fill', source: 'orig', paint: { 'fill-color': '#f97316', 'fill-opacity': 0.20 } });
        m.addLayer({ id: 'orig-line', type: 'line', source: 'orig', paint: { 'line-color': '#f97316', 'line-width': 2.5, 'line-opacity': 0.9 } });
      }

      // Layer 3: Proposed (green) — top
      if (proposedFeature?.geometry?.coordinates) {
        m.addSource('prop', { type: 'geojson', data: proposedFeature });
        m.addLayer({ id: 'prop-fill', type: 'fill', source: 'prop', paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.20 } });
        m.addLayer({ id: 'prop-line', type: 'line', source: 'prop', paint: { 'line-color': '#22c55e', 'line-width': 2.5, 'line-opacity': 0.9 } });
      }
    });

    mapRef.current = m;
    return () => {
      m.remove();
      mapRef.current = null;
      mountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasRef = !!(iso2 || iso3 || refFeature?.geometry?.coordinates);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        background: '#fff', borderBottom: '1px solid #e5e7eb', borderRadius: '10px 10px 0 0',
      }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {hasRef && (
            <svg width="18" height="12" viewBox="0 0 18 12" fill="none" style={{ marginRight: 2 }}>
              <line x1="0" y1="6" x2="18" y2="6" stroke="#3b82f6" strokeWidth="2.5" strokeDasharray="4 3"/>
            </svg>
          )}
          <div style={{ width: 14, height: 14, borderRadius: 3, background: '#f97316' }} />
          <div style={{ width: 14, height: 14, borderRadius: 3, background: '#22c55e' }} />
        </div>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>Overlay — all layers</span>
      </div>
      <div ref={containerRef} style={{ flex: 1, borderRadius: '0 0 10px 10px', overflow: 'hidden', minHeight: 200 }} />
    </div>
  );
}

// ─── Update flash banner ──────────────────────────────────────────────────────

function UpdateFlash({ revision, updatedAt }: { revision: number; updatedAt: string }) {
  return (
    <div style={{
      position: 'fixed', top: 16, right: 16, zIndex: 9999,
      background: '#16a34a', color: '#fff',
      padding: '10px 18px', borderRadius: 10,
      fontSize: 13, fontWeight: 600,
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      animation: 'slideInRight 0.3s ease',
    }}>
      <span style={{ fontSize: 16 }}>✅</span>
      <div>
        <div>Geometry updated — revision {revision}</div>
        <div style={{ fontWeight: 400, fontSize: 11, opacity: 0.85 }}>
          Maps refreshed at {new Date(updatedAt).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}

// ─── Live indicator ───────────────────────────────────────────────────────────

function LiveIndicator({ revision }: { revision: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#16a34a' }}>
      <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#16a34a', animation: 'pulse 2s infinite' }} />
      Live · rev {revision}
    </div>
  );
}

// ─── Main preview page ─────────────────────────────────────────────────────────

export default function MapPreviewPage() {
  const [data,       setData]       = useState<PreviewData | null>(null);
  const [refFeature, setRefFeature] = useState<any | null>(null);
  const [refStatus,  setRefStatus]  = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [error,      setError]      = useState<string | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [mode,       setMode]       = useState<ViewMode>('sideBySide');
  const [revision,   setRevision]   = useState(0);
  const [showFlash,  setShowFlash]  = useState(false);
  const lastUpdatedAtRef            = useRef<string | null>(null);

  const params = new URLSearchParams(window.location.search);
  const token  = params.get('token');

  // ── Load preview data ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setError('No preview token in URL. Create one via POST /api/geofence/preview.');
      setLoading(false);
      return;
    }
    fetch(`/api/geofence/preview/${token}`)
      .then(r => r.json())
      .then(json => {
        if (!json.success) throw new Error(json.error || 'Preview not found or expired');
        setData(json as PreviewData);
        lastUpdatedAtRef.current = json.updatedAt;
        setRevision(1);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // ── Fetch OSM reference boundary (optional second blue layer, same as MCP tool) ──
  useEffect(() => {
    const code = data?.iso3 || data?.iso2 || data?.countryCode;
    if (!code) return;
    setRefStatus('loading');
    fetch(`/api/mcp/get_reference_boundary?country_code=${encodeURIComponent(String(code))}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(json => {
        if (json.feature?.geometry?.coordinates) {
          setRefFeature(json.feature);
          setRefStatus('ok');
        } else {
          setRefStatus('error');
        }
      })
      .catch(() => setRefStatus('error'));
  }, [data?.iso3, data?.iso2, data?.countryCode]);

  // ── Poll for agent geometry updates every 3 s ───────────────────────────────
  const poll = useCallback(() => {
    if (!token) return;
    fetch(`/api/geofence/preview/${token}`)
      .then(r => r.json())
      .then(json => {
        if (!json.success) return;
        if (json.updatedAt && json.updatedAt !== lastUpdatedAtRef.current) {
          lastUpdatedAtRef.current = json.updatedAt;
          setData(json as PreviewData);
          setRevision(r => r + 1);
          setShowFlash(true);
          setTimeout(() => setShowFlash(false), 4000);
        }
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token || loading) return;
    const id = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [token, loading, poll]);

  // ── Derived values ──────────────────────────────────────────────────────────
  const validFeatures = data
    ? [data.originalFeature, data.proposedFeature].filter(f => f?.geometry?.coordinates?.length)
    : [];
  const bbox: [number, number, number, number] = validFeatures.length > 0
    ? computeBbox(validFeatures)
    : [-180, -85, 180, 85];

  const MAP_H  = Math.max(420, window.innerHeight - 340);
  // Remount maps when OSM GeoJSON arrives so blue fallback layers apply (async fetch).
  const mapKey = `${revision}-${mode}-${refFeature ? 'osm' : 'nom'}`;

  // ── Loading / error screens ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={pageWrap}>
        <Header />
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Loading preview…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={pageWrap}>
        <Header />
        <div style={{ padding: 40 }}>
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 20, color: '#991b1b', fontSize: 14 }}>
            <strong>Preview unavailable</strong>
            <p style={{ margin: '8px 0 0' }}>{error || 'Unknown error'}</p>
            <p style={{ margin: '8px 0 0', color: '#b45309' }}>
              Previews expire after 10 minutes. Create a new one via <code>POST /api/geofence/preview</code>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const expiryDate = new Date(data.expiresAt);
  const expired    = expiryDate < new Date();

  const refStatusLabel = {
    idle:    data?.iso3 ? `Mapbox · ${data.iso3}` : '',
    loading: data?.iso3 ? `Mapbox · ${data.iso3} + OSM loading…` : 'loading…',
    ok:      data?.iso3 ? `Mapbox · ${data.iso3} + OSM` : '✓ OSM loaded',
    error:   data?.iso3 ? `Mapbox · ${data.iso3}` : 'unavailable',
  }[refStatus];
  const refStatusColor = { idle: '#3b82f6', loading: '#f59e0b', ok: '#3b82f6', error: data?.iso3 ? '#3b82f6' : '#9ca3af' }[refStatus];

  return (
    <>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes slideInRight { from{transform:translateX(120%);opacity:0} to{transform:translateX(0);opacity:1} }
      `}</style>

      {showFlash && <UpdateFlash revision={revision} updatedAt={data.updatedAt} />}

      <div style={pageWrap}>
        <Header />

        {/* ── Meta bar ── */}
        <div style={{
          padding: '10px 20px', background: '#fff', borderBottom: '1px solid #e5e7eb',
          display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
        }}>
          {data.countryCode && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Country</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111', background: '#f3f4f6', padding: '2px 8px', borderRadius: 6 }}>
                {data.countryCode}{data.iso3 && data.iso3 !== data.countryCode ? ` (${data.iso3})` : ''}
              </span>
            </div>
          )}
          <LiveIndicator revision={revision} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}>Expires</span>
            <span style={{ fontSize: 12, color: expired ? '#dc2626' : '#16a34a' }}>
              {expired ? 'Expired' : expiryDate.toLocaleTimeString()}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}>Reference</span>
            <span style={{ fontSize: 12, color: refStatusColor }}>{refStatusLabel}</span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {(['sideBySide', 'overlay'] as ViewMode[]).map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: mode === m ? '#1d4ed8' : '#f3f4f6',
                  color:      mode === m ? '#fff' : '#374151',
                }}
              >
                {m === 'sideBySide' ? 'Side by Side' : 'Overlay'}
              </button>
            ))}
          </div>
        </div>

        {/* ── Legend ── */}
        <div style={{ padding: '8px 20px', background: '#fafafa', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <LegendItem
            customIcon={
              <svg width="24" height="14" viewBox="0 0 24 14" fill="none">
                <line x1="0" y1="7" x2="24" y2="7" stroke="#3b82f6" strokeWidth="3" strokeDasharray="5 3"/>
              </svg>
            }
            label="Blue reference (Mapbox + OSM) — trace on coasts"
          />
          <LegendItem color="#f97316" label="GeoTrust current" />
          <LegendItem color="#22c55e" label="Proposed (live)" />
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
            Green should follow blue on coasts · land borders must stay at orange
          </span>
        </div>

        {/* ── Live session hint ── */}
        <div style={{
          padding: '8px 20px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe',
          fontSize: 12, color: '#1e40af', display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <span style={{ flexShrink: 0, marginTop: 1 }}>🔄</span>
          <span>
            <strong>Live session</strong> — keep this tab open.
            Agent calls <code style={{ background: '#dbeafe', borderRadius: 3, padding: '0 4px' }}>PATCH /api/geofence/preview/{token}</code>{' '}
            with revised geometry → green layer updates automatically within 3 s.
          </span>
        </div>

        {/* ── Maps ── */}
        <div style={{ padding: 16, flex: 1 }}>
          {mode === 'sideBySide' ? (
            <div key={mapKey} style={{ display: 'flex', gap: 12, height: MAP_H }}>
              <MapPanel
                label="Original (GeoTrust)"
                color="#f97316"
                feature={data.originalFeature}
                bbox={bbox}
                iso2={data.iso2}
                iso3={data.iso3}
                refFeature={refFeature}
                missing={!data.originalFeature}
              />
              <MapPanel
                label={`Proposed · rev ${revision}`}
                color="#22c55e"
                feature={data.proposedFeature}
                bbox={bbox}
                iso2={data.iso2}
                iso3={data.iso3}
                refFeature={refFeature}
              />
            </div>
          ) : (
            <div key={mapKey} style={{ height: MAP_H, display: 'flex' }}>
              <OverlayMap
                originalFeature={data.originalFeature}
                proposedFeature={data.proposedFeature}
                bbox={bbox}
                iso2={data.iso2}
                iso3={data.iso3}
                refFeature={refFeature}
              />
            </div>
          )}
        </div>

        {/* ── Interpretation guide ── */}
        <div style={{ padding: '14px 20px', background: '#f0f9ff', borderTop: '1px solid #bae6fd' }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: '#0369a1', marginBottom: 8 }}>How to read this preview</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <Tip icon="🔵" text="Blue dashed = OSM reference boundary. This is where the authoritative border sits. The proposed green line should trace it on coastal segments." />
            <Tip icon="🟠" text="Orange = GeoTrust current data. Where orange differs from blue on a coast, that's a data gap the proposal should fix." />
            <Tip icon="🟢" text="Green = proposed (live-updating). On coastal segments it should trace the blue line + small water buffer. On land borders it should match orange exactly." />
            <Tip icon="✅" text="Good: green traces blue on coasts, green = orange on land borders, meaningful difference from orange overall." />
            <Tip icon="❌" text="Reject: green ≈ orange everywhere (no-op), green encroaches on neighbours, or uniform buffer applied instead of coastal-only trace." />
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{ padding: '10px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#6b7280' }}>
          When green correctly traces the blue reference on coasts (and land borders match orange): call{' '}
          <code style={{ background: '#e5e7eb', borderRadius: 4, padding: '1px 5px' }}>POST /api/geofence/proposals</code>.
        </div>
      </div>
    </>
  );
}

// ─── Small helpers ─────────────────────────────────────────────────────────────

function Header() {
  return (
    <div style={{ padding: '12px 20px', background: '#1d2433', display: 'flex', alignItems: 'center', gap: 12 }}>
      <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15 }}>GeoTrust</span>
      </a>
      <span style={{ color: '#64748b', fontSize: 13 }}>/ Border Preview</span>
      <div style={{ marginLeft: 'auto', fontSize: 11, color: '#475569', background: '#0f172a', padding: '3px 10px', borderRadius: 20 }}>
        Live · 3-layer comparison
      </div>
    </div>
  );
}

function LegendItem({ color, label, customIcon }: { color?: string; label: string; customIcon?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {customIcon
        ? customIcon
        : <div style={{ width: 14, height: 14, borderRadius: 3, background: color, flexShrink: 0 }} />
      }
      <span style={{ fontSize: 12, color: '#374151' }}>{label}</span>
    </div>
  );
}

function Tip({ icon, text }: { icon: string; text: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#0c4a6e', lineHeight: 1.5 }}>
      <span style={{ flexShrink: 0 }}>{icon}</span>
      <span>{text}</span>
    </div>
  );
}

const pageWrap: React.CSSProperties = {
  minHeight:     '100vh',
  display:       'flex',
  flexDirection: 'column',
  background:    '#f1f5f9',
  fontFamily:    '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};
